import type { CanBuilder, CannotBuilder, DefineAbilityForUser } from "../types";
import type {
  GetApplicationApiResponse,
  GetOrganizationByIdApiResponse,
} from "@/services/schemas";

type AiaQuestionnaireAnswerConditions = {
  status: GetApplicationApiResponse["aias"][0]["status"];
  answers: GetApplicationApiResponse["aias"][0]["sections"][0]["questions"];
};

type EpsQuestionnaireAnswerConditions = {
  status: GetApplicationApiResponse["eps"]["status"];
  answers: GetApplicationApiResponse["eps"]["sections"][0]["questions"];
};

type ApplicationUpdateConditions = {
  organization: {
    id: GetOrganizationByIdApiResponse["id"];
    status: GetOrganizationByIdApiResponse["status"];
  };
  application: GetApplicationApiResponse["status"];
};

type ApplicationSubmitConditions = {
  status: GetApplicationApiResponse["status"];
  aias: GetApplicationApiResponse["aias"];
  eps: GetApplicationApiResponse["eps"];
};

export function defineOrganizationRules({
  can,
  cannot,
  user,
}: {
  can: CanBuilder;
  cannot: CannotBuilder;
  user: DefineAbilityForUser;
}): void {
  const { organizationId } = user;
  if (!organizationId) return;

  cannot("read", "Organization");
  cannot("read", "Application");
  cannot("read", "Section");
  cannot("read", "Question");
  cannot("read", "User");

  can(
    "read",
    "AIAQuestionnaire",
    (id: GetOrganizationByIdApiResponse["id"]) => id === organizationId,
  );

  can(
    "read",
    "EPSQuestionnaire",
    (id: GetOrganizationByIdApiResponse["id"]) => id === organizationId,
  );

  can(
    "save",
    "AIAQuestionnaire",
    ({ status }: { status: GetApplicationApiResponse["aias"][0]["status"] }) =>
      status === "Em preenchimento",
  );

  can(
    "save",
    "EPSQuestionnaire",
    ({ status }: { status: GetApplicationApiResponse["eps"]["status"] }) =>
      status === "Em preenchimento",
  );

  if (user.role === "resp") {
    can(
      "answer",
      "AIAQuestionnaire",
      ({ status, answers }: AiaQuestionnaireAnswerConditions) =>
        status === "Em preenchimento" &&
        answers.every(
          ({ selectedAlternativeIds }) => selectedAlternativeIds.length > 0,
        ),
    );

    can(
      "answer",
      "EPSQuestionnaire",
      ({ status, answers }: EpsQuestionnaireAnswerConditions) =>
        status === "Em preenchimento" &&
        answers.every(({ answer }) => answer && answer.trim().length > 0),
    );

    can(
      "cancel",
      "AIAQuestionnaire",
      ({
        status,
      }: { status: GetApplicationApiResponse["aias"][0]["status"] }) =>
        status === "Em preenchimento",
    );

    can(
      "cancel",
      "EPSQuestionnaire",
      ({ status }: { status: GetApplicationApiResponse["eps"]["status"] }) =>
        status === "Em preenchimento",
    );

    can(
      "update",
      "Organization",
      ({
        id,
        status,
      }: {
        id: GetOrganizationByIdApiResponse["id"];
        status: GetOrganizationByIdApiResponse["status"];
      }) => id === organizationId && status === "ativ",
    );

    can(
      "create",
      "Application",
      ({
        id,
        status,
      }: {
        id: GetOrganizationByIdApiResponse["id"];
        status: GetOrganizationByIdApiResponse["status"];
      }) => id === organizationId && status === "ativ",
    );

    can(
      "update",
      "Application",
      ({ organization, application }: ApplicationUpdateConditions) =>
        organization.id === organizationId &&
        organization.status === "ativ" &&
        application === "Em preenchimento",
    );

    can(
      "delete",
      "Application",
      ({ organization, application }: ApplicationUpdateConditions) =>
        organization.id === organizationId &&
        organization.status === "ativ" &&
        (application === "Em preenchimento" || application === "Homologada"),
    );

    can(
      "submit",
      "Application",
      ({ status, aias, eps }: ApplicationSubmitConditions) =>
        status === "Em preenchimento" &&
        aias.every((aia) => aia.status === "Preenchido") &&
        eps.status === "Preenchido",
    );

    can(
      "cancel",
      "Application",
      ({ status }: { status: GetApplicationApiResponse["status"] }) =>
        status === "Submetida para avaliação",
    );

    can(
      "review",
      "Application",
      ({
        evaluation,
      }: { evaluation: GetApplicationApiResponse["evaluation"]["status"] }) =>
        evaluation === "Aprovada",
    );
  }
}
